import styled from '@emotion/styled'
import { Button } from '@nextui-org/react-latest'
import StarIcon from '@public/static/icons/referrals-landing/starIcon'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  onBtnClick: () => void
}

const images = [
  '/images/benefits1.jpeg',
  '/images/benefits2.jpeg',
  '/images/benefits3.jpeg',
  '/images/benefits4.jpeg',
  '/images/benefits5.jpeg',
]

const Benefits = ({ onBtnClick }: Props) => {
  const [currentImage, setCurrentImage] = useState(0)
  const sliderRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const { t } = useTranslation('referrals')

  // Check if the element is in the viewport
  const isInView = () => {
    if (!sliderRef.current) return false
    const rect = sliderRef.current.getBoundingClientRect()
    return rect.top >= 0 && rect.bottom <= window.innerHeight
  }

  // Function to start the image switching timer
  const startTimer = () => {
    if (timerRef.current) return // Avoid starting multiple timers
    timerRef.current = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length)
    }, 2000)
  }

  // Function to stop the timer
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  // Handle manual change
  const handleManualChange = (index) => {
    stopTimer()
    setCurrentImage(index)
    startTimer()
  }

  // Effect to start/stop the timer based on scroll visibility
  useEffect(() => {
    const handleScroll = () => {
      if (isInView()) {
        startTimer() // Start the timer if in view
      } else {
        stopTimer() // Stop the timer if out of view
      }
    }
    window.addEventListener('scroll', handleScroll)
    // Cleanup on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll)
      stopTimer()
    }
  }, [images.length])

  // Reset the timer when the current image is manually set
  useEffect(() => {
    stopTimer()
    startTimer()
  }, [currentImage])

  return (
    <Container>
      <Left>
        <Title>{t('trip_promo.title')}</Title>
        <Description>{t('trip_promo.description')}</Description>
        <Button
          className="px-14 py-7 text-base font-regular max-w-max ml-[1rem] lg:ml-[2.5rem]"
          radius="full"
          color="primary"
          onPress={onBtnClick}
        >
          {t('trip_promo.button_text')}
        </Button>
      </Left>
      <Right className="relative" ref={sliderRef}>
        <div className="absolute -top-14 right-1">
          <div className="relative">
            <StarIcon />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 inline-flex flex-col items-center text-center">
              <Span1>{t('trip_promo.star_icon.days')}</Span1>
              <div className="inline-flex gap-1">
                <Span2>{t('trip_promo.star_icon.for_label')}</Span2>
                <Span3>{t('trip_promo.star_icon.people')}</Span3>
              </div>
              <Span4>{t('trip_promo.star_icon.all_inclusive')}</Span4>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <Image
              width={1000}
              height={600}
              alt=""
              className="object-cover w-full rounded-xl max-h-[388px]"
              src={images[currentImage]}
              loading="lazy"
            />
          </div>
          <div className="h-[2px] bg-uguGray w-full" />
          <div className="flex lg:gap-4 gap-2 items-center">
            {images.map((img, i) => (
              <Image
                width={200}
                height={200}
                className="max-w-[calc(100%/5-6px)] lg:max-w-[calc(100%/5-12px)] aspect-[16/11] max-h-[102px] flex-1 object-cover rounded-lg cursor-pointer"
                alt=""
                key={img}
                src={img}
                onClick={() => handleManualChange(i)}
                loading="lazy"
              />
            ))}
          </div>
          <div className="flex justify-center items-center gap-2">
            {images.map((_, i) => (
              <div
                className={`h-2.5 w-2.5 rounded-full cursor-pointer transition-all duration-75 active:scale-110 ${
                  currentImage === i ? 'bg-uguBrightPurple' : 'bg-uguGray'
                }`}
                key={i}
                onClick={() => handleManualChange(i)}
              />
            ))}
          </div>
        </div>
      </Right>
    </Container>
  )
}

export default Benefits

const Container = styled.div`
  max-width: 1450px;
  width: 100%;
  margin: 3rem auto;
  padding: 3rem 0rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3rem;
  background-color: #f7f8ff;
  flex-direction: ${(props) => props.direction || 'row'};
  border-radius: 1rem;
  @media (max-width: 1466px) {
    margin: 3rem 1rem;
    width: calc(100% - 2rem);
  }
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: start;
    justify-content: flex-start;
  }
`
const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  flex: 1;
`

const Title = styled.div`
  background: #e8eafd;
  font-family: var(--font-roboto);
  font-weight: 600;
  font-size: 38px;
  line-height: 49px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #1a1e3d;
  max-width: 640px;
  border-top-right-radius: 1rem;
  border-bottom-right-radius: 1rem;
  padding: 1.5rem;
  padding-left: 2.5rem;
  @media (max-width: 769px) {
    font-size: 33px;
    line-height: 40px;
  }
  @media (max-width: 1024px) {
    padding-left: 1rem;
  }
`
const Description = styled.div`
  font-family: var(--font-roboto);
  font-size: 20px;
  font-weight: 400;
  line-height: 30px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #53566e;
  padding-left: 2.5rem;
  max-width: 600px;
  @media (max-width: 1024px) {
    padding-left: 1rem;
  }
`
const Right = styled.div`
  flex: 1;
  padding: 2.5rem;
  @media (max-width: 1024px) {
    padding: 1rem;
  }
`
const Span1 = styled.div`
  font-family: var(--font-roboto);
  font-size: 36px;
  font-weight: 700;
  line-height: 36px;
  text-align: center;
  color: white;
  white-space: nowrap; /* Prevents Safari from breaking lines unexpectedly */
`

const Span2 = styled.div`
  font-family: var(--font-roboto);
  font-size: 14px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
  color: white;
  display: inline-block; /* Ensures proper spacing and alignment */
`

const Span3 = styled.div`
  font-family: var(--font-roboto);
  font-size: 23px;
  font-weight: 700;
  line-height: 29px;
  text-align: center;
  color: white;
  display: inline-block; /* Ensures proper spacing and alignment */
`

const Span4 = styled.div`
  font-family: var(--font-roboto);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  color: white;
  margin-top: 4px;
`
